import type { CSSProperties } from 'react';

/**
 * Glassmorphism kol.my mark — aligned with docs/kol_my_glassmorphism_logo_v3.html.
 * Nav variant scales all structural ratios from the hero (64px wordmark) so the
 * main card vs slogan pill stay proportionally consistent.
 *
 * Theming: default follows `html.dark` via `.kol-my-glass-logo` in index.css (light
 * mesh = opaque frost + deep wordmark; dark = translucent v3). Use
 * `forDarkBackground` on marketing/footer logos so they keep the dark treatment
 * even when the global theme is light.
 */
type KolMyGlassLogoProps = {
  variant?: 'nav' | 'hero';
  className?: string;
  /** Slogan pill. Defaults: hidden for `nav`, shown for `hero`. */
  showTagline?: boolean;
  /** Use on #0a0a0f / footer etc. so the mark stays high-contrast when the app theme is light. */
  forDarkBackground?: boolean;
  /**
   * Scales the full hero mark (card, wordmark, slogan, shards, orbs). Ignored for `nav`.
   * Use e.g. `0.6` for ~40% smaller than the default hero.
   */
  heroScale?: number;
  /** Defaults to the v3.html scene label. */
  'aria-label'?: string;
};

type ShardSpec = {
  w: number;
  h: number;
  rotate: number;
  r: number;
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
};

/** Hero metrics from v3.html; nav = same ratios at ~22px wordmark (≈ 1.375rem). */
const V3_HERO = {
  logoPx: 64,
  logoLetterPx: -2,
  card: { pt: 18, px: 36, pb: 16, r: 10 },
  dividerMy: 2,
  wrapGap: 14,
  badge: { py: 8, px: 20, gap: 8, dot: 7, font: 12, letterSpacing: 1.5 },
  shards: [
    { w: 40, h: 40, top: -16, right: -16, rotate: 15, r: 10 },
    { w: 24, h: 24, bottom: -10, left: -12, rotate: -20, r: 6 },
    { w: 16, h: 16, top: 20, left: -11, rotate: 35, r: 4 },
  ] satisfies ShardSpec[],
};

const NAV_LOGO_PX = 22;

export default function KolMyGlassLogo({
  variant = 'nav',
  className = '',
  showTagline,
  forDarkBackground = false,
  heroScale,
  'aria-label': ariaLabel = 'kol.my glassmorphism logo',
}: KolMyGlassLogoProps) {
  const isNav = variant === 'nav';
  const showPill = showTagline ?? !isNav;
  const baseS = isNav ? NAV_LOGO_PX / V3_HERO.logoPx : 1;
  const s = baseS * (isNav ? 1 : (heroScale ?? 1));

  const logoPx = V3_HERO.logoPx * s;
  const logoLetter = V3_HERO.logoLetterPx * s;
  const cardPt = V3_HERO.card.pt * s;
  const cardPx = V3_HERO.card.px * s;
  const cardPb = V3_HERO.card.pb * s;
  const cardR = V3_HERO.card.r * s;
  const wrapGap = V3_HERO.wrapGap * s;
  const dividerMy = V3_HERO.dividerMy * s;

  const badgePy = V3_HERO.badge.py * s;
  const badgePx = V3_HERO.badge.px * s;
  const badgeGap = V3_HERO.badge.gap * s;
  const badgeDot = Math.max(5, V3_HERO.badge.dot * s);
  /** Keep pill readable in the bar; hero scales with `heroScale`. */
  const badgeFont = isNav ? Math.max(9, V3_HERO.badge.font * s) : Math.max(8, V3_HERO.badge.font * s);
  const badgeLetter = V3_HERO.badge.letterSpacing * s;

  const glassCardStyle: CSSProperties = {
    padding: `${cardPt}px ${cardPx}px ${cardPb}px`,
    borderRadius: `${cardR}px`,
    background: 'var(--kol-logo-card-bg)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    borderTop: '1px solid var(--kol-logo-border-top)',
    borderLeft: '1px solid var(--kol-logo-border-left)',
    borderRight: '1px solid var(--kol-logo-border-right)',
    borderBottom: '1px solid var(--kol-logo-border-bottom)',
    boxShadow: 'var(--kol-logo-card-shadow)',
  };

  const badgeStyle: CSSProperties = {
    padding: `${badgePy}px ${badgePx}px`,
    gap: `${badgeGap}px`,
    borderRadius: 9999,
    background: 'var(--kol-logo-badge-bg)',
    border: '0.5px solid var(--kol-logo-badge-border)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    boxShadow: 'var(--kol-logo-badge-shadow)',
  };

  const rootClass = [
    'kol-my-glass-logo relative inline-flex flex-col items-center',
    forDarkBackground ? 'kol-my-glass-logo--dark-surface' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className={rootClass}
      data-logo-variant={variant}
    >
      {/* Ambient orbs — same palette as v3.html */}
      <div
        className={`kol-logo-orb-wrap pointer-events-none absolute ${isNav ? '-inset-2' : '-inset-10'}`}
        aria-hidden
      >
        <div
          className="absolute rounded-full bg-[#7c3aed] blur-2xl"
          style={{
            width: isNav ? 72 : 220 * s,
            height: isNav ? 72 : 220 * s,
            top: isNav ? -12 : -40 * s,
            left: isNav ? -16 : -40 * s,
            opacity: 'var(--kol-logo-orb-opacity)',
          }}
        />
        <div
          className="absolute rounded-full bg-[#0ea5e9] blur-2xl"
          style={{
            width: isNav ? 56 : 180 * s,
            height: isNav ? 56 : 180 * s,
            bottom: isNav ? -10 : -30 * s,
            right: isNav ? -6 : 60 * s,
            opacity: 'var(--kol-logo-orb-opacity)',
          }}
        />
        <div
          className="absolute rounded-full bg-[#ec4899] blur-2xl"
          style={{
            width: isNav ? 48 : 140 * s,
            height: isNav ? 48 : 140 * s,
            top: isNav ? '45%' : '60%',
            left: isNav ? '52%' : '55%',
            opacity: 'var(--kol-logo-orb-opacity)',
          }}
        />
      </div>

      <div className="relative flex flex-col items-center" style={{ gap: `${wrapGap}px` }}>
          <div className="relative" style={glassCardStyle}>
            {/* Top glass sheen (v3 ::before) */}
            <div
              className="pointer-events-none absolute left-0 right-0 top-0 rounded-t-[inherit] rounded-b-none"
              style={{
                height: '50%',
                borderRadius: `${cardR}px ${cardR}px 0 0`,
                background: 'var(--kol-logo-sheen)',
              }}
              aria-hidden
            />

            {V3_HERO.shards.map((sh, i) => {
              const shardStyle: CSSProperties = {
                width: `${sh.w * s}px`,
                height: `${sh.h * s}px`,
                transform: `rotate(${sh.rotate}deg)`,
                borderRadius: `${sh.r * s}px`,
                background: 'var(--kol-logo-shard-bg)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                border: '0.5px solid var(--kol-logo-shard-border)',
              };
              if (sh.top !== undefined) shardStyle.top = `${sh.top * s}px`;
              if (sh.right !== undefined) shardStyle.right = `${sh.right * s}px`;
              if (sh.bottom !== undefined) shardStyle.bottom = `${sh.bottom * s}px`;
              if (sh.left !== undefined) shardStyle.left = `${sh.left * s}px`;
              return <div key={i} className="pointer-events-none absolute" style={shardStyle} aria-hidden />;
            })}

            <span
              className="font-logo relative block font-extrabold leading-none"
              style={{
                fontSize: `${logoPx}px`,
                letterSpacing: `${logoLetter}px`,
                background: 'var(--kol-logo-word-gradient)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                filter: 'var(--kol-logo-text-filter)',
              }}
            >
              kol
              <span
                style={{
                  background: 'var(--kol-logo-dotmy-gradient)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                .my
              </span>
            </span>

            <div
              className="pointer-events-none w-full"
              style={{
                height: 0.5,
                marginTop: `${dividerMy}px`,
                marginBottom: `${dividerMy}px`,
                background: 'var(--kol-logo-divider)',
              }}
              aria-hidden
            />
          </div>

          {showPill && (
            <div className="flex max-w-[min(100vw-2rem,280px)] items-center sm:max-w-none" style={badgeStyle}>
              <span
                className="shrink-0 motion-reduce:animate-none animate-kol-logo-dot-pulse rounded-full"
                style={{
                  width: `${badgeDot}px`,
                  height: `${badgeDot}px`,
                  background: 'radial-gradient(circle at 35% 35%, #a78bfa, #7c3aed)',
                }}
                aria-hidden
              />
              <span
                className="font-logo font-bold whitespace-nowrap text-transparent"
                style={{
                  fontSize: `${badgeFont}px`,
                  letterSpacing: `${badgeLetter}px`,
                  background: 'var(--kol-logo-badge-text)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                }}
              >
                Influencer marketing, leveled up
              </span>
            </div>
          )}
      </div>
    </div>
  );
}
